import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    employees: 100,
    avgHourlyWage: 25,
    workingDays: 250,
    hoursPerDay: 8,
    incidentFrequency: 50,
    avgIncidentResolutionTime: 3,
    currentErrorRate: 15,
  });

  const [results, setResults] = useState({
    incidentReductionSavings: 0,
    resolutionTimeSavings: 0,
    errorReductionSavings: 0,
    productivityGain: 0,
    totalAnnualBenefit: 0,
    annualCostOfPlatform: 0,
    netAnnualBenefit: 0,
    paybackPeriodDays: 0,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prevInputs) => ({
      ...prevInputs,
      [name]: parseFloat(value) || 0,
    }));
  };

  const calculateROI = () => {
    const {
      employees,
      avgHourlyWage,
      workingDays,
      hoursPerDay,
      incidentFrequency,
      avgIncidentResolutionTime,
      currentErrorRate,
    } = inputs;

    // Realistic improvement factors based on industry benchmarks
    const incidentReductionFactor = 0.3; // 30% reduction in incidents
    const resolutionTimeImprovementFactor = 0.25; // 25% faster resolution
    const errorReductionFactor = 0.1; // 10% reduction in errors
    const productivityImprovementFactor = 0.03; // 3% overall productivity improvement

    // Calculate benefits
    const annualWorkHours = employees * workingDays * hoursPerDay;
    const incidentReductionSavings =
      incidentFrequency *
      12 *
      avgIncidentResolutionTime *
      avgHourlyWage *
      incidentReductionFactor;
    const resolutionTimeSavings =
      incidentFrequency *
      12 *
      avgIncidentResolutionTime *
      avgHourlyWage *
      resolutionTimeImprovementFactor;
    const errorReductionSavings =
      annualWorkHours *
      avgHourlyWage *
      (currentErrorRate / 100) *
      errorReductionFactor;
    const productivityGain =
      annualWorkHours * avgHourlyWage * productivityImprovementFactor;
    const totalAnnualBenefit =
      incidentReductionSavings +
      resolutionTimeSavings +
      errorReductionSavings +
      productivityGain;

    const monthlyPlatformCost = 1000; // Fixed cost
    const annualCostOfPlatform = monthlyPlatformCost * 12;
    const netAnnualBenefit = totalAnnualBenefit - annualCostOfPlatform;
    const paybackPeriodDays =
      totalAnnualBenefit > 0
        ? ((annualCostOfPlatform / totalAnnualBenefit) * 365).toFixed(0)
        : Infinity;

    setResults({
      incidentReductionSavings,
      resolutionTimeSavings,
      errorReductionSavings,
      productivityGain,
      totalAnnualBenefit,
      annualCostOfPlatform,
      netAnnualBenefit,
      paybackPeriodDays,
    });
  };

  useEffect(() => {
    calculateROI();
  }, [inputs]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatAxisTick = (value) => {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      compactDisplay: "short",
    }).format(value);
  };

  const benefitBreakdown = [
    { name: "Incident Reduction", value: results.incidentReductionSavings },
    { name: "Resolution Time", value: results.resolutionTimeSavings },
    { name: "Error Reduction", value: results.errorReductionSavings },
    { name: "Productivity Gain", value: results.productivityGain },
  ];

  const cumulativeBenefitData = Array.from({ length: 24 }, (_, i) => ({
    month: i + 1,
    benefit: (results.totalAnnualBenefit / 12) * (i + 1),
    cost: (results.annualCostOfPlatform / 12) * (i + 1),
  }));

  const inputFields = [
    {
      name: "employees",
      label: "Number of Employees",
      unit: "",
      tooltip: "Total number of employees using the platform",
    },
    {
      name: "avgHourlyWage",
      label: "Average Hourly Wage",
      unit: "$/hour",
      tooltip: "Average hourly wage of employees",
    },
    {
      name: "workingDays",
      label: "Working Days per Year",
      unit: "days",
      tooltip: "Number of working days in a year",
    },
    {
      name: "hoursPerDay",
      label: "Working Hours per Day",
      unit: "hours",
      tooltip: "Number of working hours in a day",
    },
    {
      name: "incidentFrequency",
      label: "Incidents per Month",
      unit: "",
      tooltip: "Average number of incidents per month",
    },
    {
      name: "avgIncidentResolutionTime",
      label: "Average Incident Resolution Time",
      unit: "hours",
      tooltip: "Average time taken to resolve an incident",
    },
    {
      name: "currentErrorRate",
      label: "Current Error Rate",
      unit: "%",
      tooltip: "Current percentage of errors in processes",
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* <Typography
        variant="h2"
        component="div"
        gutterBottom
        sx={{ color: "primary.main", fontWeight: "bold" }}
      >
        Calculate Arma's ROI
      </Typography> */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography
                variant="h5"
                paddingBottom={1.6}
                sx={{ color: "primary.dark", fontWeight: "bold" }}
              >
                Fill Out Your Use Case
              </Typography>
              <Grid container spacing={2}>
                {inputFields.map(({ name, label, unit, tooltip }) => (
                  <Grid item xs={12} key={name}>
                    <Tooltip title={tooltip}>
                      <TextField
                        fullWidth
                        label={`${label} ${unit ? `(${unit})` : ""}`}
                        name={name}
                        type="number"
                        value={inputs[name]}
                        onChange={handleInputChange}
                        InputProps={{
                          endAdornment: (
                            <IconButton size="small">
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          ),
                        }}
                      />
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
              {/* <Button
                variant="contained"
                color="primary"
                onClick={calculateROI}
                sx={{ mt: 2 }}
              >
                Calculate ROI
              </Button> */}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "primary.dark", fontWeight: "bold" }}
                  >
                    ROI Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Total Annual Benefit
                      </Typography>
                      <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold'}}>
                        {formatCurrency(results.totalAnnualBenefit)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Annual Platform Cost
                      </Typography>
                      <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold'}}>
                        {formatCurrency(results.annualCostOfPlatform)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Payback Period
                      </Typography>
                      <Typography
                        variant="h5"
                        color="primary.main"
                        sx={{ fontWeight: 'bold'}}
                      >{`${results.paybackPeriodDays} days`}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "primary.dark", fontWeight: "bold" }}
                  >
                    Benefit Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={benefitBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={formatAxisTick} />
                      <RechartsTooltip
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "primary.dark", fontWeight: "bold" }}
                  >
                    Cumulative Benefit
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={cumulativeBenefitData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={formatAxisTick} />
                      <RechartsTooltip
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="benefit"
                        stroke="#4CAF50"
                        name="Cumulative Benefit"
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#F44336"
                        name="Cumulative Cost"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {/* <Card>
            <CardContent>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: "primary.dark", fontWeight: "medium" }}
              >
                Explanation of Productivity Gains
              </Typography>
              <Typography variant="body1" paragraph>
                The productivity gains calculated in this ROI model are based on
                several factors that a Connected Worker Platform typically
                improves:
              </Typography>
              <Typography variant="body1" component="div">
                <ol>
                  <li>
                    <strong>Streamlined Processes:</strong> By digitizing
                    workflows and providing easy access to information, workers
                    can complete tasks more efficiently.
                  </li>
                  <li>
                    <strong>Reduced Downtime:</strong> Quick access to
                    documentation and expert support helps resolve issues
                    faster, minimizing production interruptions.
                  </li>
                  <li>
                    <strong>Improved Communication:</strong> Real-time
                    collaboration features reduce delays in information sharing
                    and decision-making.
                  </li>
                  <li>
                    <strong>Enhanced Training:</strong> On-the-job guidance and
                    digital training materials help workers learn new skills
                    faster and retain information better.
                  </li>
                  <li>
                    <strong>Data-Driven Insights:</strong> Analytics from the
                    platform can help identify bottlenecks and optimization
                    opportunities in workflows.
                  </li>
                </ol>
              </Typography>
              <Typography variant="body1" paragraph>
                The model assumes a conservative 5% overall productivity
                improvement based on these factors. However, actual results may
                vary depending on your specific implementation and industry. We
                recommend monitoring actual performance improvements after
                implementing the platform to validate and refine these
                estimates.
              </Typography>
            </CardContent>
          </Card> */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ROICalculator;
